import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  GraduationCap,
  Globe,
  Edit,
  Save,
  X,
  Bookmark,
  Linkedin,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "../../../../context/AuthContext";
import api from "../../../../services/api";
import {
  card,
  surfaceMuted,
  pageTitle,
  sectionTitle,
  microLabel,
  accentBarSection,
  accentBarCard,
  badgeBase,
  emptyState,
  motionFadeUp,
  motionStaggerChild,
} from "../../../../lib/design-system";
import { cn } from "../../../../lib/utils";
import {
  Skeleton,
  SkeletonCard,
} from "../../../StudentDashboard/Components/ui/Skeleton";

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const ProfileSkeleton = () => (
  <div className="py-6">
    <div className="space-y-6">
      {/* Header skeleton */}
      <SkeletonCard className="overflow-hidden">
        <Skeleton className="w-full h-1.5" />
        <div className="p-6 flex flex-col sm:flex-row items-center gap-6">
          <Skeleton className="w-24 h-24 sm:w-28 sm:h-28" />
          <div className="flex-grow space-y-3 w-full">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
      </SkeletonCard>

      {/* Professional details skeleton */}
      <SkeletonCard className="p-6 space-y-4">
        <Skeleton className="h-5 w-44" />
        <Skeleton className="h-1 w-12" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-20 w-full" />
        <div className="flex gap-3">
          <Skeleton className="h-8 w-28" />
          <Skeleton className="h-8 w-20" />
          <Skeleton className="h-8 w-24" />
        </div>
      </SkeletonCard>

      {/* Contact skeleton */}
      <SkeletonCard className="p-6 space-y-4">
        <Skeleton className="h-5 w-40" />
        <Skeleton className="h-1 w-12" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className={cn(
                surfaceMuted,
                "rounded-sm border border-gray-100 dark:border-gray-700/50 p-3 space-y-2"
              )}
            >
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-5 w-full" />
            </div>
          ))}
        </div>
      </SkeletonCard>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const TeacherProfilePage = () => {
  const { user, updateUser } = useAuth();

  // State for teacher details
  const [teacherDetails, setTeacherDetails] = useState({
    name: "",
    department: "",
    position: "",
    facultyId: "",
    email: "",
    phone: "",
    officeLocation: "",
    specialization: "",
  });

  useEffect(() => {
    if (user) {
      setTeacherDetails((prev) => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email,
        phone: user.mobileNo || prev.phone,
        position: user.role || prev.position,
      }));
    }
  }, [user]);

  const userInitials = useMemo(() => {
    const n = user?.name || "";
    return n
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join("");
  }, [user]);

  // New state for mentor details
  const [mentorDetails, setMentorDetails] = useState({
    profTitle: "",
    profDesc: "",
    googleScholarLink: "",
    scopusLink: "",
    linkedInLink: "",
  });

  // Load mentor details from user object if available
  useEffect(() => {
    if (user) {
      setMentorDetails((prev) => ({
        ...prev,
        profTitle: user.profTitle || "",
        profDesc: user.profDesc || "",
        googleScholarLink: user.googleScholarLink || "",
        scopusLink: user.scopusLink || "",
        linkedInLink: user.linkedInLink || "",
      }));
    }
  }, [user]);

  // Edit mode states
  const [editingSection, setEditingSection] = useState(null);

  // Toggle edit mode for sections
  const toggleEdit = (section) => {
    if (editingSection === section) {
      setEditingSection(null);
    } else {
      setEditingSection(section);
    }
  };

  // Handle input change for mentor details
  const handleMentorChange = (e) => {
    const { name, value } = e.target;
    setMentorDetails({
      ...mentorDetails,
      [name]: value,
    });
  };

  // Update teacher profile API function
  const updateTeacherProfile = async (profileData) => {
    try {
      const response = await api.put("/teachers/profile", profileData);
      const data = response.data;

      if (data.success) {
        alert("Profile updated successfully!");
        return data.teacher;
      } else {
        alert(`Error: ${data.message || "Failed to update profile"}`);
        return null;
      }
    } catch (error) {
      console.error("Failed to update profile:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update profile. Please try again.";
      alert(errorMessage);
      return null;
    }
  };

  // Save mentor details changes
  const saveMentorDetails = async () => {
    const profileData = {
      profTitle: mentorDetails.profTitle,
      profDesc: mentorDetails.profDesc,
      googleScholarLink: mentorDetails.googleScholarLink,
      scopusLink: mentorDetails.scopusLink,
      linkedInLink: mentorDetails.linkedInLink,
    };

    const updatedTeacher = await updateTeacherProfile(profileData);
    if (updatedTeacher) {
      setEditingSection(null);
      setMentorDetails((prev) => ({
        ...prev,
        profTitle: updatedTeacher.profTitle || prev.profTitle,
        profDesc: updatedTeacher.profDesc || prev.profDesc,
        googleScholarLink:
          updatedTeacher.googleScholarLink || prev.googleScholarLink,
        scopusLink: updatedTeacher.scopusLink || prev.scopusLink,
        linkedInLink: updatedTeacher.linkedInLink || prev.linkedInLink,
      }));
      updateUser({
        profTitle: updatedTeacher.profTitle,
        profDesc: updatedTeacher.profDesc,
        googleScholarLink: updatedTeacher.googleScholarLink,
        scopusLink: updatedTeacher.scopusLink,
        linkedInLink: updatedTeacher.linkedInLink,
      });
    }
  };

  const hasAnyProfessionalDetails =
    mentorDetails.profTitle ||
    mentorDetails.profDesc ||
    mentorDetails.googleScholarLink ||
    mentorDetails.scopusLink ||
    mentorDetails.linkedInLink;

  const hasLinks =
    mentorDetails.googleScholarLink ||
    mentorDetails.scopusLink ||
    mentorDetails.linkedInLink;

  if (!user) return <ProfileSkeleton />;

  return (
    <motion.div {...motionFadeUp} className="py-6">
      <div className="space-y-6">
        {/* ── Profile Header Card ──────────────────────────── */}

        <motion.div
          {...motionStaggerChild(0)}
          className={cn(card, "overflow-hidden border-gray-100 dark:border-gray-700/50")}
        >
          <div className={cn(accentBarCard, "w-full h-1.5")} />
          <div className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Square avatar with initials */}
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-sm overflow-hidden flex items-center justify-center bg-accent1/10">
                <span className="text-3xl sm:text-4xl font-black text-accent1">
                  {userInitials || "T"}
                </span>
              </div>

              {/* Name and role */}
              <div className="flex-grow text-center sm:text-left">
                <h1 className={pageTitle}>{teacherDetails.name}</h1>
                <div className="mt-3 flex flex-wrap justify-center sm:justify-start gap-3 items-center">
                  <span
                    className={cn(
                      badgeBase,
                      "text-accent1 bg-accent1/10 border-accent1/30"
                    )}
                  >
                    <GraduationCap className="h-3 w-3 inline mr-1.5" />
                    Faculty
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Professional Details + Contact Info — side by side ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Professional Details Card — wider */}
          <motion.div
            {...motionStaggerChild(1)}
            className={cn(card, "overflow-hidden border-gray-100 dark:border-gray-700/50 lg:col-span-8")}
          >
            <div className="p-6 space-y-4">
              {/* Section header */}
              <div className="flex justify-between items-start">
                <div>
                  <h2 className={sectionTitle}>Professional Details</h2>
                  <div className={cn(accentBarSection, "mt-2")} />
                </div>
                <button
                  onClick={() => toggleEdit("mentor")}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm transition-colors"
                  aria-label={
                    editingSection === "mentor"
                      ? "Cancel editing"
                      : "Edit details"
                  }
                >
                  {editingSection === "mentor" ? (
                    <X className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  ) : (
                    <Edit className="w-4 h-4 text-accent1" />
                  )}
                </button>
              </div>

              {/* Content */}
              <AnimatePresence mode="wait">
                {editingSection === "mentor" ? (
                  <motion.div
                    key="edit"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    <div className="space-y-2">
                      <label className={microLabel}>Professional Title</label>
                      <input
                        type="text"
                        name="profTitle"
                        value={mentorDetails.profTitle}
                        onChange={handleMentorChange}
                        placeholder="e.g., Associate Professor of Computer Science"
                        maxLength={200}
                        className="w-full p-3 border border-gray-200/60 dark:border-gray-700/60 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors placeholder-gray-400 dark:placeholder-gray-500"
                      />
                    </div>

                    <div className="space-y-2">
                      <label className={microLabel}>
                        Professional Description
                      </label>
                      <textarea
                        name="profDesc"
                        value={mentorDetails.profDesc}
                        onChange={handleMentorChange}
                        rows={4}
                        placeholder="Share your expertise, research interests, and professional background..."
                        maxLength={2000}
                        className="w-full p-3 border border-gray-200/60 dark:border-gray-700/60 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors placeholder-gray-400 dark:placeholder-gray-500 resize-none"
                      />
                    </div>

                    {/* Social Links Editor */}
                    <div className="space-y-3">
                      <label className={microLabel}>Professional Links</label>
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <label className={cn(microLabel, "text-gray-400 dark:text-gray-500")}>
                            Google Scholar
                          </label>
                          <input
                            type="url"
                            name="googleScholarLink"
                            value={mentorDetails.googleScholarLink}
                            onChange={handleMentorChange}
                            placeholder="https://scholar.google.com/..."
                            className="w-full p-3 border border-gray-200/60 dark:border-gray-700/60 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors placeholder-gray-400 dark:placeholder-gray-500"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className={cn(microLabel, "text-gray-400 dark:text-gray-500")}>
                            Scopus
                          </label>
                          <input
                            type="url"
                            name="scopusLink"
                            value={mentorDetails.scopusLink}
                            onChange={handleMentorChange}
                            placeholder="https://www.scopus.com/..."
                            className="w-full p-3 border border-gray-200/60 dark:border-gray-700/60 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors placeholder-gray-400 dark:placeholder-gray-500"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className={cn(microLabel, "text-gray-400 dark:text-gray-500")}>
                            LinkedIn
                          </label>
                          <input
                            type="url"
                            name="linkedInLink"
                            value={mentorDetails.linkedInLink}
                            onChange={handleMentorChange}
                            placeholder="https://www.linkedin.com/in/..."
                            className="w-full p-3 border border-gray-200/60 dark:border-gray-700/60 rounded-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1 focus:border-accent1 focus:ring-opacity-50 transition-colors placeholder-gray-400 dark:placeholder-gray-500"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <button
                        onClick={saveMentorDetails}
                        className="px-6 py-3 bg-accent1 text-white rounded-sm shadow-sm font-bold uppercase tracking-wider text-sm flex items-center gap-2 transition-colors hover:opacity-90"
                      >
                        <Save className="w-4 h-4" />
                        Save Changes
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="view"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-4"
                  >
                    {hasAnyProfessionalDetails ? (
                      <>
                        {mentorDetails.profTitle && (
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-accent1/10 rounded-sm flex-shrink-0">
                              <Bookmark className="w-4 h-4 text-accent1" />
                            </div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white">
                              {mentorDetails.profTitle}
                            </p>
                          </div>
                        )}

                        {mentorDetails.profDesc && (
                          <div
                            className={cn(
                              surfaceMuted,
                              "rounded-sm border border-gray-100 dark:border-gray-700/50 p-4"
                            )}
                          >
                            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                              {mentorDetails.profDesc}
                            </p>
                          </div>
                        )}

                        {hasLinks && (
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <Globe className="w-4 h-4 text-accent1" />
                              <span className={microLabel}>
                                Professional Links
                              </span>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                              {mentorDetails.googleScholarLink && (
                                <a
                                  href={mentorDetails.googleScholarLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    badgeBase,
                                    "text-accent1 bg-accent1/10 border-accent1/30 inline-flex items-center gap-1.5 hover:bg-accent1/20 transition-colors"
                                  )}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Google Scholar
                                </a>
                              )}
                              {mentorDetails.scopusLink && (
                                <a
                                  href={mentorDetails.scopusLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    badgeBase,
                                    "text-accent1 bg-accent1/10 border-accent1/30 inline-flex items-center gap-1.5 hover:bg-accent1/20 transition-colors"
                                  )}
                                >
                                  <ExternalLink className="w-3 h-3" />
                                  Scopus
                                </a>
                              )}
                              {mentorDetails.linkedInLink && (
                                <a
                                  href={mentorDetails.linkedInLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={cn(
                                    badgeBase,
                                    "text-accent1 bg-accent1/10 border-accent1/30 inline-flex items-center gap-1.5 hover:bg-accent1/20 transition-colors"
                                  )}
                                >
                                  <Linkedin className="w-3 h-3" />
                                  LinkedIn
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className={emptyState}>
                        <GraduationCap className="h-10 w-10 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No professional details added yet.
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          Click the edit button to add your information.
                        </p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Contact Information Card — narrower sidebar */}
          <motion.div
            {...motionStaggerChild(2)}
            className={cn(card, "p-6 space-y-4 border-gray-100 dark:border-gray-700/50 lg:col-span-4")}
          >
            <div>
              <h2 className={sectionTitle}>Contact Information</h2>
              <div className={cn(accentBarSection, "mt-2")} />
            </div>

            <div className="space-y-3">
              {/* Email */}
              <div
                className={cn(
                  surfaceMuted,
                  "rounded-sm border border-gray-100 dark:border-gray-700/50 p-3"
                )}
              >
                <div className={microLabel}>
                  <Mail className="w-3 h-3 inline mr-1.5" />
                  Email
                </div>
                <div className="text-gray-800 dark:text-gray-200 mt-1 text-sm truncate">
                  {teacherDetails.email || "Not provided"}
                </div>
              </div>

              {/* Phone */}
              <div
                className={cn(
                  surfaceMuted,
                  "rounded-sm border border-gray-100 dark:border-gray-700/50 p-3"
                )}
              >
                <div className={microLabel}>
                  <Phone className="w-3 h-3 inline mr-1.5" />
                  Phone
                </div>
                <div className="text-gray-800 dark:text-gray-200 mt-1 text-sm truncate">
                  {teacherDetails.phone || "Not provided"}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default TeacherProfilePage;
